import { Bell, Home, Megaphone, Smartphone } from "lucide-react";
import type { ReactNode } from "react";

import { getLocalizedHref, stripLocalePrefix } from "#/components/layout/PublicChrome.js";
import { createT } from "#/lib/i18n.js";

type SuperadminChromeProps = {
  children: ReactNode;
  locale: string;
  urlPathname?: string;
};

const NAV_ITEMS = [
  { href: "/superadmin", labelKey: "nav.home", Icon: Home },
  { href: "/superadmin/pwa", labelKey: "nav.pwa", Icon: Smartphone },
  { href: "/superadmin/ads", labelKey: "nav.ads", Icon: Megaphone },
  { href: "/superadmin/subscribers", labelKey: "nav.subscribers", Icon: Bell },
] as const;

export function shouldShowSuperadminChrome(pathname: string | undefined): boolean {
  const logicalPath = stripLocalePrefix(pathname ?? "/");
  const firstSegment = logicalPath.split("/").filter(Boolean)[0];
  return firstSegment === "superadmin";
}

function isActiveNavItem(currentPathname: string | undefined, href: string): boolean {
  const logicalPath = stripLocalePrefix(currentPathname ?? "/");
  if (href === "/superadmin") {
    return logicalPath === "/superadmin" || logicalPath === "/superadmin/";
  }
  return logicalPath === href || logicalPath.startsWith(`${href}/`);
}

export function SuperadminChrome({ children, locale, urlPathname }: SuperadminChromeProps) {
  const t = createT(locale, "superadmin");

  return (
    <>
      <a href="#main-content" className="skip-link">
        {t("skipToContent")}
      </a>
      <header
        data-superadmin-chrome="header"
        className="border-b border-[color:var(--color-border-subtle)] bg-[color:var(--color-bg-base)]"
      >
        <div className="container mx-auto flex flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <a
            href={getLocalizedHref(locale, "/superadmin")}
            className="inline-flex min-h-11 items-center gap-2 text-base font-semibold text-[color:var(--color-fg-default)] no-underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-accent-info)]"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md border border-[color:var(--color-border-default)] bg-[color:var(--color-bg-surface)]">
              <Home className="h-4 w-4" aria-hidden="true" />
            </span>
            {t("title")}
          </a>
          <nav aria-label={t("nav.label")} className="overflow-x-auto">
            <ul className="flex min-h-11 items-center gap-1">
              {NAV_ITEMS.map((item) => {
                const active = isActiveNavItem(urlPathname, item.href);
                const Icon = item.Icon;
                return (
                  <li key={item.href} className="shrink-0">
                    <a
                      href={getLocalizedHref(locale, item.href)}
                      aria-current={active ? "page" : undefined}
                      className={[
                        "inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-medium no-underline transition-colors duration-150",
                        active
                          ? "bg-[color:var(--color-bg-surface)] text-[color:var(--color-fg-default)] ring-1 ring-[color:var(--color-border-default)]"
                          : "text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-surface)] hover:text-[color:var(--color-fg-default)]",
                      ].join(" ")}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {t(item.labelKey)}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </header>
      {children}
    </>
  );
}
